import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { ABSOLUTE_META, type AbsoluteParams } from './absolute.model';

/** Absolute params with index signature for JsxParamStore compatibility */
type AbsoluteParamsIndexed = AbsoluteParams & { [key: string]: number };

/**
 * Creates absolute value functions: a|x-h| + k
 *
 * Parameters:
 * - a: vertical scale/reflection
 * - h: horizontal shift (vertex x-coordinate)
 * - k: vertical shift (vertex y-coordinate)
 */
export class AbsoluteCreator extends BaseFunctionCreator<AbsoluteParamsIndexed> {
  readonly functionType = 'absolute';
  readonly defaultParams: AbsoluteParamsIndexed = { ...ABSOLUTE_META.defaultParams };
  readonly availableFeatures = ABSOLUTE_META.availableFeatures;
  readonly handleConfigs = ABSOLUTE_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as AbsoluteParamsIndexed;

    const colorName = (display?.curve?.color ?? 'orange') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: ABSOLUTE_META.defaultRanges.a },
      h: { value: initialParams.h, range: ABSOLUTE_META.defaultRanges.h },
      k: { value: initialParams.k, range: ABSOLUTE_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      return a * Math.abs(x - h) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // Derivative of a|x-h| + k is a * sign(x-h), undefined at x = h
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          if (x === h) {
            return NaN;
          }
          return a * Math.sign(x - h);
        };
      }
      // Higher order derivatives are 0 everywhere except at x = h
      return (x: number) => {
        const h = paramStore.getValue('h');
        if (x === h) {
          return NaN;
        }
        return 0;
      };
    };

    return {
      id,
      type: 'absolute',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as AbsoluteParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
